import React, { useState, useMemo, useCallback, useEffect } from "react";
import {
  Box,
  Container,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  Tooltip,
  CircularProgress,
  Alert,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Collapse,
  Stepper,
  Step,
  StepLabel,
  List,
  ListItemButton,
  ListItemText,
  ListItemIcon,
  Checkbox,
  InputAdornment,
  Autocomplete,
  Chip as MuiChip,
  Tabs,
  Tab,
} from "@mui/material";
import { isAxiosError } from "axios";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import RefreshIcon from "@mui/icons-material/Refresh";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import SearchIcon from "@mui/icons-material/Search";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import SyncIcon from "@mui/icons-material/Sync";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { useSnackbar } from "notistack";
import { useOrganization } from "../../contexts/OrganizationContext";
import {
  useSyncConfigs,
  useCreateSyncConfig,
  useUpdateSyncConfig,
  useDeleteSyncConfig,
  useBackfillSyncConfig,
  useSyncSourceOptions,
} from "../../api/hooks/useSyncConfigs";
import type {
  SyncConfigDto,
  CreateSyncConfigDto,
  UpdateSyncConfigDto,
  BackfillRequestDto,
  QuestionOption,
} from "../../api/types/dtos/sync";
import LoadingSpinner from "../../components/LoadingSpinner";
import QuestionMappingEditor from "./components/QuestionMappingEditor";
import JsonPreview from "./components/JsonPreview";
import { SyncQueueTab, SyncRunsTab } from "./components/SyncMonitorTabs";
import { PRIVILEGES } from "../../api/types/dtos";
import { useHasPrivilege } from "../../components/authorization";
import {
  SyncSelectedQuestion,
  QuestionMappings,
  templateToJson,
  jsonToTemplate,
  questionOptionToSelected,
} from "./types/mapping";

const WIZARD_STEPS = ["General", "Origen", "Destino", "Mapeo", "Revision"];

const DEFAULT_AUTH_CONFIG = `{
  "method": "POST",
  "url": "https://client.example.com/auth/token",
  "bodyTemplate": {
    "client_id": "{{clientId}}",
    "username": "{{username}}",
    "password": "{{password}}"
  },
  "credentials": {
    "clientId": "",
    "username": "",
    "password": ""
  },
  "tokenExtraction": {
    "type": "json_path",
    "path": "$.access_token"
  },
  "tokenHeader": "Authorization",
  "tokenPrefix": "Bearer "
}`;

const DEFAULT_API_CONFIG = `{
  "method": "POST",
  "url": "https://client.example.com/api/documents",
  "headers": {
    "Content-Type": "application/json"
  },
  "payloadWrapper": null
}`;

const WRAPPED_API_CONFIG = `{
  "method": "POST",
  "url": "https://client.example.com/api/documents",
  "headers": {
    "Content-Type": "application/json"
  },
  "payloadWrapper": {
    "key": "JsonIn",
    "serialize": true
  }
}`;

interface SourceSelection {
  entityType: string;
  questionIds: string[];
}

interface WizardState {
  name: string;
  description: string;
  processIntervalSeconds: string;
  sourceSelection: SourceSelection;
  sourceConfigJsonRaw: string;
  useRawSourceConfig: boolean;
  authConfigJson: string;
  apiConfigJson: string;
  questionMappings: QuestionMappings;
  rawTemplateJson: string;
}

const EMPTY_SOURCE_SELECTION: SourceSelection = {
  entityType: "answer",
  questionIds: [],
};

const EMPTY_STATE: WizardState = {
  name: "",
  description: "",
  processIntervalSeconds: "60",
  sourceSelection: EMPTY_SOURCE_SELECTION,
  sourceConfigJsonRaw: "",
  useRawSourceConfig: false,
  authConfigJson: DEFAULT_AUTH_CONFIG,
  apiConfigJson: DEFAULT_API_CONFIG,
  questionMappings: {},
  rawTemplateJson: "",
};

const buildSourceConfigJson = (sel: SourceSelection): string => {
  const filter: Record<string, unknown> = {};
  if (sel.questionIds.length > 0) {
    filter.questionIds = sel.questionIds;
  }
  return JSON.stringify({ entityType: sel.entityType || "answer", filter }, null, 2);
};

const parseSourceConfigJson = (json: string): SourceSelection | null => {
  try {
    const parsed = JSON.parse(json);
    if (!parsed || typeof parsed !== "object") return null;
    const filter = parsed.filter ?? {};
    return {
      entityType: parsed.entityType ?? "answer",
      questionIds: Array.isArray(filter.questionIds)
        ? filter.questionIds.filter((q: unknown): q is string => typeof q === "string")
        : [],
    };
  } catch {
    return null;
  }
};

const getStateFromConfig = (config: SyncConfigDto): WizardState => {
  const parsedSource = parseSourceConfigJson(config.sourceConfigJson);
  return {
    name: config.name,
    description: config.description ?? "",
    processIntervalSeconds: String(config.processIntervalSeconds),
    sourceSelection: parsedSource ?? EMPTY_SOURCE_SELECTION,
    sourceConfigJsonRaw: config.sourceConfigJson,
    useRawSourceConfig: !parsedSource,
    authConfigJson: config.authConfigJson,
    apiConfigJson: config.apiConfigJson,
    questionMappings: {},
    rawTemplateJson: "",
  };
};

const validateJson = (json: string): string | null => {
  if (!json.trim()) return null;
  try {
    JSON.parse(json);
    return null;
  } catch {
    return "JSON invalido";
  }
};

interface JsonSectionProps {
  title: string;
  value: string;
  onChange: (value: string) => void;
  error: string | null;
  defaultOpen?: boolean;
}

const JsonSection: React.FC<JsonSectionProps> = ({
  title,
  value,
  onChange,
  error,
  defaultOpen = false,
}) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <Box sx={{ mb: 1 }}>
      <Button
        onClick={() => setOpen(!open)}
        startIcon={
          <ExpandMoreIcon
            sx={{
              transform: open ? "rotate(0deg)" : "rotate(-90deg)",
              transition: "transform 0.2s",
            }}
          />
        }
        sx={{ textTransform: "none", justifyContent: "flex-start", pl: 0 }}
        color="inherit"
      >
        <Typography variant="subtitle2">{title}</Typography>
      </Button>
      <Collapse in={open}>
        <TextField
          fullWidth
          multiline
          rows={8}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          error={!!error}
          size="small"
          sx={{
            "& .MuiInputBase-input": {
              fontFamily: "monospace",
              fontSize: "0.75rem",
            },
          }}
        />
        {error && (
          <Alert severity="error" sx={{ mt: 0.5 }}>
            {error}
          </Alert>
        )}
      </Collapse>
    </Box>
  );
};

interface SourceStepProps {
  selection: SourceSelection;
  onSelectionChange: (sel: SourceSelection) => void;
  useRaw: boolean;
  rawJson: string;
  onRawJsonChange: (json: string) => void;
  onToggleUseRaw: (useRaw: boolean) => void;
}

const SourceStep: React.FC<SourceStepProps> = ({
  selection,
  onSelectionChange,
  useRaw,
  rawJson,
  onRawJsonChange,
  onToggleUseRaw,
}) => {
  const { data: options, isLoading, error } = useSyncSourceOptions();
  const [questionSearch, setQuestionSearch] = useState("");
  const [programFilter, setProgramFilter] = useState<string | null>(null);
  const [versionFilter, setVersionFilter] = useState<string | null>(null);
  const [itemFilter, setItemFilter] = useState<string | null>(null);

  const allQuestions: QuestionOption[] = useMemo(() => options?.questions ?? [], [options]);
  const programs = useMemo(
    () => Array.from(new Set(allQuestions.map((q) => q.programName))).sort(),
    [allQuestions]
  );
  const versions = useMemo(
    () =>
      Array.from(
        new Set(
          allQuestions
            .filter((q) => !programFilter || q.programName === programFilter)
            .map((q) => q.versionName)
        )
      ).sort(),
    [allQuestions, programFilter]
  );
  const items = useMemo(
    () =>
      Array.from(
        new Set(
          allQuestions
            .filter(
              (q) =>
                (!programFilter || q.programName === programFilter) &&
                (!versionFilter || q.versionName === versionFilter)
            )
            .map((q) => q.itemName)
        )
      ).sort(),
    [allQuestions, programFilter, versionFilter]
  );

  const filteredQuestions = useMemo(() => {
    const term = questionSearch.trim().toLowerCase();
    return allQuestions.filter((q) => {
      if (programFilter && q.programName !== programFilter) return false;
      if (versionFilter && q.versionName !== versionFilter) return false;
      if (itemFilter && q.itemName !== itemFilter) return false;
      if (!term) return true;
      return (
        q.label.toLowerCase().includes(term) ||
        q.programName.toLowerCase().includes(term) ||
        q.itemName.toLowerCase().includes(term)
      );
    });
  }, [allQuestions, questionSearch, programFilter, versionFilter, itemFilter]);

  const selectedSet = useMemo(() => new Set(selection.questionIds), [selection.questionIds]);

  const toggleQuestion = (id: string) => {
    const next = new Set(selectedSet);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    onSelectionChange({ ...selection, questionIds: Array.from(next) });
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Typography variant="body2" color="text.secondary">
          Configura qué datos se sincronizarán y desde dónde.
        </Typography>
        <Button
          size="small"
          color={useRaw ? "primary" : "inherit"}
          variant={useRaw ? "outlined" : "text"}
          onClick={() => onToggleUseRaw(!useRaw)}
          startIcon={<ExpandMoreIcon sx={{ transform: "rotate(-90deg)" }} />}
        >
          {useRaw ? "Volver a modo visual" : "Modo avanzado (JSON)"}
        </Button>
      </Box>

      {useRaw ? (
        <JsonSection
          title="Configuración de Origen (JSON)"
          value={rawJson}
          onChange={onRawJsonChange}
          error={validateJson(rawJson)}
          defaultOpen
        />
      ) : (
        <>
          <Alert severity="info">
            Las sincronizaciones se activan cuando se guarda o actualiza una respuesta de inspección
            en una finca hija de tu organización.
          </Alert>

          <Paper variant="outlined" sx={{ p: 2 }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                mb: 1,
              }}
            >
              <Box>
                <Typography variant="subtitle2">Filtrar por preguntas (opcional)</Typography>
                <Typography variant="caption" color="text.secondary">
                  Si seleccionas preguntas, solo se sincronizarán respuestas a esas preguntas.
                  Déjalo vacío para sincronizar todas.
                </Typography>
              </Box>
              {selection.questionIds.length > 0 && (
                <Chip
                  size="small"
                  color="primary"
                  variant="outlined"
                  label={`${selection.questionIds.length} seleccionada(s)`}
                  onDelete={() => onSelectionChange({ ...selection, questionIds: [] })}
                  deleteIcon={<DeleteIcon />}
                />
              )}
            </Box>

            {isLoading && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, py: 2 }}>
                <CircularProgress size={16} />
                <Typography variant="body2" color="text.secondary">
                  Cargando preguntas...
                </Typography>
              </Box>
            )}
            {error && isAxiosError(error) && error.response?.status === 403 ? (
              <Alert severity="warning">
                No tienes permisos para ver las preguntas disponibles.
              </Alert>
            ) : (
              error && (
                <Alert severity="warning">
                  No se pudieron cargar las preguntas. Usa el modo avanzado (JSON).
                </Alert>
              )
            )}

            {!isLoading && !error && allQuestions.length === 0 && (
              <Alert severity="info">No hay preguntas disponibles para esta organización.</Alert>
            )}

            {!isLoading && !error && allQuestions.length > 0 && (
              <>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Buscar pregunta..."
                  value={questionSearch}
                  onChange={(e) => setQuestionSearch(e.target.value)}
                  sx={{ mb: 1 }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                />
                <Box sx={{ display: "flex", gap: 1, mb: 1, flexWrap: "wrap" }}>
                  <Autocomplete
                    size="small"
                    options={programs}
                    value={programFilter}
                    onChange={(_, v) => {
                      setProgramFilter(v);
                      setVersionFilter(null);
                      setItemFilter(null);
                    }}
                    renderInput={(p) => (
                      <TextField {...p} label="Programa" sx={{ minWidth: 180 }} />
                    )}
                    sx={{ minWidth: 180, flex: "1 1 180px" }}
                  />
                  <Autocomplete
                    size="small"
                    options={versions}
                    value={versionFilter}
                    onChange={(_, v) => {
                      setVersionFilter(v);
                      setItemFilter(null);
                    }}
                    renderInput={(p) => <TextField {...p} label="Versión" sx={{ minWidth: 140 }} />}
                    sx={{ minWidth: 140, flex: "1 1 140px" }}
                  />
                  <Autocomplete
                    size="small"
                    options={items}
                    value={itemFilter}
                    onChange={(_, v) => setItemFilter(v)}
                    renderInput={(p) => <TextField {...p} label="Item" sx={{ minWidth: 180 }} />}
                    sx={{ minWidth: 180, flex: "1 1 180px" }}
                  />
                </Box>
                <Paper variant="outlined" sx={{ maxHeight: 280, overflow: "auto" }}>
                  <List disablePadding dense>
                    {filteredQuestions.map((q) => {
                      const checked = selectedSet.has(q.questionId);
                      return (
                        <ListItemButton
                          key={q.questionId}
                          onClick={() => toggleQuestion(q.questionId)}
                          selected={checked}
                          dense
                        >
                          <ListItemIcon sx={{ minWidth: 36 }}>
                            <Checkbox edge="start" checked={checked} tabIndex={-1} size="small" />
                          </ListItemIcon>
                          <ListItemText
                            primary={
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 1,
                                }}
                              >
                                <Typography variant="body2" component="span">
                                  {q.label}
                                </Typography>
                                <MuiChip
                                  label={q.questionType}
                                  size="small"
                                  color={q.isFile ? "warning" : "default"}
                                  variant="outlined"
                                  sx={{ fontSize: "0.65rem", height: 18 }}
                                />
                              </Box>
                            }
                            secondary={`${q.programName} / ${q.versionName} / ${q.itemName}`}
                            secondaryTypographyProps={{
                              sx: { fontSize: "0.75rem" },
                            }}
                          />
                        </ListItemButton>
                      );
                    })}
                    {filteredQuestions.length === 0 && (
                      <ListItemText
                        sx={{ px: 2, py: 2 }}
                        primary="No se encontraron preguntas con los filtros actuales."
                      />
                    )}
                  </List>
                </Paper>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: "block", mt: 1 }}
                >
                  <FilterAltIcon fontSize="inherit" sx={{ verticalAlign: "middle" }} />{" "}
                  {filteredQuestions.length} de {allQuestions.length} preguntas mostradas.
                </Typography>
              </>
            )}
          </Paper>

          <JsonPreview
            json={buildSourceConfigJson(selection)}
            label="JSON generado (vista previa)"
            maxHeight={150}
          />
        </>
      )}
    </Box>
  );
};

const SyncConfigWizardDialog: React.FC<{
  open: boolean;
  editConfig: SyncConfigDto | null;
  onClose: () => void;
}> = ({ open, editConfig, onClose }) => {
  const { enqueueSnackbar } = useSnackbar();
  const createMutation = useCreateSyncConfig();
  const updateMutation = useUpdateSyncConfig();

  const [activeStep, setActiveStep] = useState(0);
  const [state, setState] = useState<WizardState>(EMPTY_STATE);

  useEffect(() => {
    if (open) {
      if (editConfig) {
        setState(getStateFromConfig(editConfig));
        if (editConfig.templateS3Url) {
          fetch(editConfig.templateS3Url)
            .then((res) => {
              if (!res.ok) throw new Error(`HTTP ${res.status}`);
              return res.text();
            })
            .then((templateJson) => {
              const parsed = templateJson ? jsonToTemplate(templateJson) : null;
              if (parsed) {
                setState((prev) => ({
                  ...prev,
                  questionMappings: parsed.questionMappings,
                  rawTemplateJson: templateJson,
                }));
              }
            })
            .catch(() => {
              enqueueSnackbar("No se pudo cargar el template de mapeo desde S3", {
                variant: "warning",
              });
            });
        }
      } else {
        setState(EMPTY_STATE);
      }
      setActiveStep(0);
    }
  }, [open, editConfig, enqueueSnackbar]);

  const update = useCallback((updates: Partial<WizardState>) => {
    setState((prev) => ({ ...prev, ...updates }));
  }, []);

  const { data: sourceOptionsData } = useSyncSourceOptions();
  const sourceOptionsQuestions: QuestionOption[] = useMemo(
    () => sourceOptionsData?.questions ?? [],
    [sourceOptionsData]
  );

  const derivedSelectedQuestions: SyncSelectedQuestion[] = useMemo(() => {
    const questionIds = new Set(state.sourceSelection.questionIds);
    if (questionIds.size === 0) return [];
    return sourceOptionsQuestions
      .filter((q) => questionIds.has(q.questionId))
      .map((q) => questionOptionToSelected(q, q.questionId));
  }, [state.sourceSelection.questionIds, sourceOptionsQuestions]);

  const jsonErrors = useMemo(
    () => ({
      authConfig: validateJson(state.authConfigJson),
      apiConfig: validateJson(state.apiConfigJson),
      sourceConfigRaw: state.useRawSourceConfig ? validateJson(state.sourceConfigJsonRaw) : null,
    }),
    [state.authConfigJson, state.apiConfigJson, state.useRawSourceConfig, state.sourceConfigJsonRaw]
  );

  const hasJsonErrors = Object.values(jsonErrors).some((e) => e !== null);
  const isSubmitting = createMutation.isPending || updateMutation.isPending;

  const sourceConfigJsonForSubmit = useMemo(() => {
    if (state.useRawSourceConfig) return state.sourceConfigJsonRaw;
    return buildSourceConfigJson(state.sourceSelection);
  }, [state.useRawSourceConfig, state.sourceSelection, state.sourceConfigJsonRaw]);

  const templateJsonForSubmit = useMemo(() => {
    const fromTemplate = templateToJson(derivedSelectedQuestions, state.questionMappings);
    if (fromTemplate !== '{"selectedQuestions":[],"questionMappings":{}}') return fromTemplate;
    return state.rawTemplateJson.trim() || fromTemplate;
  }, [derivedSelectedQuestions, state.questionMappings, state.rawTemplateJson]);

  const canProceed = useMemo(() => {
    switch (activeStep) {
      case 0:
        return state.name.trim() !== "";
      case 1:
        return !jsonErrors.sourceConfigRaw;
      case 2:
        return !jsonErrors.authConfig && !jsonErrors.apiConfig;
      case 3: {
        const hasMapping = Object.values(state.questionMappings).some((rows) =>
          rows.some((r) => r.targetField.trim() !== "")
        );
        return hasMapping || state.rawTemplateJson.trim() !== "";
      }
      default:
        return true;
    }
  }, [activeStep, state.name, state.questionMappings, state.rawTemplateJson, jsonErrors]);

  const handleSubmit = async () => {
    if (!state.name.trim()) {
      enqueueSnackbar("El nombre es obligatorio", { variant: "error" });
      return;
    }

    try {
      if (editConfig) {
        const updateData: UpdateSyncConfigDto = {
          name: state.name,
          description: state.description || undefined,
          sourceConfigJson: sourceConfigJsonForSubmit,
          authConfigJson: state.authConfigJson,
          apiConfigJson: state.apiConfigJson,
          processIntervalSeconds: parseInt(state.processIntervalSeconds, 10) || 60,
          templateJson: templateJsonForSubmit,
        };
        await updateMutation.mutateAsync({ id: editConfig.id, data: updateData });
        enqueueSnackbar("Configuracion actualizada", { variant: "success" });
      } else {
        const createData: CreateSyncConfigDto = {
          name: state.name,
          description: state.description || undefined,
          sourceConfigJson: sourceConfigJsonForSubmit,
          authConfigJson: state.authConfigJson,
          apiConfigJson: state.apiConfigJson,
          templateJson: templateJsonForSubmit,
          processIntervalSeconds: parseInt(state.processIntervalSeconds, 10) || 60,
        };
        await createMutation.mutateAsync(createData);
        enqueueSnackbar("Configuracion creada", { variant: "success" });
      }
      onClose();
    } catch {
      enqueueSnackbar("Error al guardar la configuracion", { variant: "error" });
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography variant="h6">
            {editConfig ? "Editar Configuracion" : "Nueva Configuracion de Sincronizacion"}
          </Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ minHeight: 500 }}>
        <Stepper activeStep={activeStep} sx={{ my: 2 }}>
          {WIZARD_STEPS.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {activeStep === 0 && (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 2,
              maxWidth: 500,
            }}
          >
            <TextField
              label="Nombre"
              value={state.name}
              onChange={(e) => update({ name: e.target.value })}
              fullWidth
              required
              error={!state.name.trim()}
              helperText={!state.name.trim() ? "El nombre es obligatorio" : ""}
            />
            <TextField
              label="Descripcion"
              value={state.description}
              onChange={(e) => update({ description: e.target.value })}
              fullWidth
              multiline
              rows={2}
            />
            <TextField
              label="Intervalo de procesamiento (segundos)"
              value={state.processIntervalSeconds}
              onChange={(e) => update({ processIntervalSeconds: e.target.value })}
              type="number"
              sx={{ maxWidth: 250 }}
              helperText="Cada cuanto tiempo se ejecutara la sincronizacion"
            />
          </Box>
        )}

        {activeStep === 1 && (
          <SourceStep
            selection={state.sourceSelection}
            onSelectionChange={(selection) => update({ sourceSelection: selection })}
            useRaw={state.useRawSourceConfig}
            rawJson={state.sourceConfigJsonRaw}
            onRawJsonChange={(json) => update({ sourceConfigJsonRaw: json })}
            onToggleUseRaw={(useRaw) => update({ useRawSourceConfig: useRaw })}
          />
        )}

        {activeStep === 2 && (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <Alert severity="info">
              Configura la autenticacion y el endpoint de la API del cliente externo.
            </Alert>
            <JsonSection
              title="Configuracion de Auth (JSON)"
              value={state.authConfigJson}
              onChange={(v) => update({ authConfigJson: v })}
              error={jsonErrors.authConfig}
              defaultOpen
            />
            <JsonSection
              title="Configuracion de API (JSON)"
              value={state.apiConfigJson}
              onChange={(v) => update({ apiConfigJson: v })}
              error={jsonErrors.apiConfig}
              defaultOpen
            />
            <Alert severity="info" icon={<FilterAltIcon />}>
              <Typography variant="body2" sx={{ mb: 1 }}>
                Algunos clientes requieren que el payload vaya serializado como string dentro de un
                campo (ej. <code>{'{ "JsonIn": "{...}" }'}</code>). Usa los presets para
                configurarlo rapidamente.
              </Typography>
              <Box sx={{ display: "flex", gap: 1, mt: 1 }}>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => update({ apiConfigJson: WRAPPED_API_CONFIG })}
                >
                  Payload en JsonIn (stringificado)
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  color="inherit"
                  onClick={() => update({ apiConfigJson: DEFAULT_API_CONFIG })}
                >
                  Payload directo (sin wrapper)
                </Button>
              </Box>
            </Alert>
          </Box>
        )}

        {activeStep === 3 && (
          <QuestionMappingEditor
            selectedQuestions={derivedSelectedQuestions}
            questionMappings={state.questionMappings}
            onQuestionMappingsChange={(mappings) => update({ questionMappings: mappings })}
          />
        )}

        {activeStep === 4 && (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                Resumen
              </Typography>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: 1,
                }}
              >
                <Typography variant="body2">
                  <strong>Nombre:</strong> {state.name}
                </Typography>
                <Typography variant="body2">
                  <strong>Intervalo:</strong> {state.processIntervalSeconds}s
                </Typography>
                <Typography variant="body2">
                  <strong>Entidad:</strong> {state.sourceSelection.entityType}
                </Typography>
                <Typography variant="body2">
                  <strong>Filtro preguntas:</strong>{" "}
                  {state.sourceSelection.questionIds.length > 0
                    ? `${state.sourceSelection.questionIds.length}`
                    : "Todas"}
                </Typography>
                <Typography variant="body2">
                  <strong>Preguntas seleccionadas:</strong> {derivedSelectedQuestions.length}
                </Typography>
                <Typography variant="body2">
                  <strong>Preguntas mapeadas:</strong>{" "}
                  {
                    Object.values(state.questionMappings).filter((rows) =>
                      rows.some((r) => r.targetField.trim() !== "")
                    ).length
                  }{" "}
                  de {derivedSelectedQuestions.length}
                </Typography>
              </Box>
            </Paper>

            <JsonPreview
              json={sourceConfigJsonForSubmit}
              label="Configuracion de Origen"
              maxHeight={150}
            />
            <JsonPreview
              json={state.authConfigJson}
              label="Configuracion de Auth"
              maxHeight={150}
            />
            <JsonPreview json={state.apiConfigJson} label="Configuracion de API" maxHeight={150} />
            <JsonPreview json={templateJsonForSubmit} label="Template de Mapeo" maxHeight={200} />

            {hasJsonErrors && (
              <Alert severity="error">
                Hay errores de JSON en la configuracion. Vuelve a los pasos anteriores para
                corregirlos.
              </Alert>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} color="inherit">
          Cancelar
        </Button>
        {activeStep > 0 && (
          <Button onClick={() => setActiveStep((prev) => prev - 1)} disabled={isSubmitting}>
            Atras
          </Button>
        )}
        {activeStep < WIZARD_STEPS.length - 1 ? (
          <Button
            variant="contained"
            onClick={() => setActiveStep((prev) => prev + 1)}
            disabled={!canProceed}
          >
            Siguiente
          </Button>
        ) : (
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={hasJsonErrors || isSubmitting || !state.name.trim()}
            startIcon={isSubmitting ? <CircularProgress size={16} /> : undefined}
          >
            {editConfig ? "Actualizar" : "Crear"}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

const DeleteConfirmDialog: React.FC<{
  open: boolean;
  config: SyncConfigDto | null;
  onClose: () => void;
}> = ({ open, config, onClose }) => {
  const { enqueueSnackbar } = useSnackbar();
  const deleteMutation = useDeleteSyncConfig();

  const handleDelete = async () => {
    if (!config) return;
    try {
      await deleteMutation.mutateAsync(config.id);
      enqueueSnackbar("Configuracion eliminada", { variant: "success" });
      onClose();
    } catch {
      enqueueSnackbar("Error al eliminar la configuracion", { variant: "error" });
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Confirmar Eliminacion</DialogTitle>
      <DialogContent>
        <Typography>
          Seguro que deseas eliminar la configuracion <strong>{config?.name}</strong>?
        </Typography>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} color="inherit">
          Cancelar
        </Button>
        <Button
          variant="contained"
          color="error"
          onClick={handleDelete}
          disabled={deleteMutation.isPending}
          startIcon={deleteMutation.isPending ? <CircularProgress size={16} /> : undefined}
        >
          Eliminar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const BackfillDialog: React.FC<{
  open: boolean;
  config: SyncConfigDto | null;
  onClose: () => void;
}> = ({ open, config, onClose }) => {
  const { enqueueSnackbar } = useSnackbar();
  const backfillMutation = useBackfillSyncConfig();
  const [fromDate, setFromDate] = useState<Date | null>(null);
  const [toDate, setToDate] = useState<Date | null>(null);

  React.useEffect(() => {
    if (open) {
      setFromDate(null);
      setToDate(null);
    }
  }, [open]);

  const handleBackfill = async () => {
    if (!config) return;
    try {
      const data: BackfillRequestDto = {};
      if (fromDate) data.fromDate = fromDate.toISOString();
      if (toDate) data.toDate = toDate.toISOString();
      const result = await backfillMutation.mutateAsync({
        id: config.id,
        data,
      });
      enqueueSnackbar(
        `${result.enqueued} item(s) enqueued for sync (${result.skippedAlreadySynced} already synced)`,
        { variant: "success" }
      );
      onClose();
    } catch {
      enqueueSnackbar("Error al ejecutar el backfill", { variant: "error" });
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Backfill / Resync</DialogTitle>
      <DialogContent>
        <Alert severity="info" sx={{ mb: 2 }}>
          Esto buscara respuestas existentes y las encolara para sincronizacion. Los items ya
          sincronizados o pendientes seran omitidos.
        </Alert>
        <Typography variant="body2" sx={{ mb: 2 }}>
          Config: <strong>{config?.name}</strong>
        </Typography>
        <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DatePicker
              label="Desde (opcional)"
              value={fromDate}
              onChange={(v) => setFromDate(v)}
              slotProps={{ textField: { size: "small", fullWidth: true } }}
            />
            <DatePicker
              label="Hasta (opcional)"
              value={toDate}
              onChange={(v) => setToDate(v)}
              slotProps={{ textField: { size: "small", fullWidth: true } }}
            />
          </LocalizationProvider>
        </Box>
        <Typography variant="caption" color="text.secondary">
          Deja las fechas vacias para sincronizar todo el historial.
        </Typography>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} color="inherit">
          Cancelar
        </Button>
        <Button
          variant="contained"
          onClick={handleBackfill}
          disabled={backfillMutation.isPending}
          startIcon={backfillMutation.isPending ? <CircularProgress size={16} /> : <SyncIcon />}
        >
          {backfillMutation.isPending ? "Procesando..." : "Encolar"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const SyncConfigsPage: React.FC = () => {
  const { selectedOrganization } = useOrganization();
  const { hasPrivilege: canCreateSync } = useHasPrivilege(PRIVILEGES.CREATE_SYNC_CONFIG);
  const { hasPrivilege: canEditSync } = useHasPrivilege(PRIVILEGES.UPDATE_SYNC_CONFIG);
  const { hasPrivilege: canDeleteSync } = useHasPrivilege(PRIVILEGES.DELETE_SYNC_CONFIG);
  const [createOpen, setCreateOpen] = useState(false);
  const [editConfig, setEditConfig] = useState<SyncConfigDto | null>(null);
  const [deleteConfig, setDeleteConfig] = useState<SyncConfigDto | null>(null);
  const [backfillConfig, setBackfillConfig] = useState<SyncConfigDto | null>(null);
  const [activeTab, setActiveTab] = useState(0);

  const { data: configs, isLoading, error, refetch, isRefetching } = useSyncConfigs();

  if (!selectedOrganization) {
    return (
      <Container maxWidth="lg">
        <Paper sx={{ p: 3, textAlign: "center" }}>
          <Typography variant="h6" color="text.secondary">
            Selecciona una organizacion para ver las configuraciones.
          </Typography>
        </Paper>
      </Container>
    );
  }

  const configList = configs ?? [];

  return (
    <Container maxWidth="lg">
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          my: 4,
        }}
      >
        <Box>
          <Typography variant="h4" fontWeight={700}>
            Sincronizacion
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Configuraciones de sincronizacion de datos
          </Typography>
        </Box>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button
            variant="outlined"
            onClick={() => refetch()}
            startIcon={isRefetching ? <CircularProgress size={16} /> : <RefreshIcon />}
            disabled={isRefetching}
          >
            Actualizar
          </Button>
          {canCreateSync && (
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => setCreateOpen(true)}>
              Nuevo Sync
            </Button>
          )}
        </Box>
      </Box>

      <Tabs
        value={activeTab}
        onChange={(_, v) => setActiveTab(v)}
        sx={{ borderBottom: 1, borderColor: "divider", mb: 3 }}
      >
        <Tab label="Configuraciones" />
        <Tab label="Cola de Sincronizacion" />
        <Tab label="Ejecuciones" />
      </Tabs>

      {activeTab === 1 ? (
        <SyncQueueTab />
      ) : activeTab === 2 ? (
        <SyncRunsTab />
      ) : isLoading ? (
        <LoadingSpinner />
      ) : error && isAxiosError(error) && error.response?.status === 403 ? (
        <Paper sx={{ p: 4, textAlign: "center" }}>
          <Alert severity="warning" sx={{ justifyContent: "center" }}>
            No tienes permisos para ver las configuraciones de sincronizacion. Contacta a un
            administrador si necesitas acceso.
          </Alert>
        </Paper>
      ) : error ? (
        <Paper sx={{ p: 4, textAlign: "center" }}>
          <Alert severity="error" sx={{ justifyContent: "center" }}>
            Error al cargar las configuraciones. Intenta de nuevo.
          </Alert>
          <Button
            sx={{ mt: 2 }}
            variant="outlined"
            onClick={() => refetch()}
            startIcon={<RefreshIcon />}
          >
            Reintentar
          </Button>
        </Paper>
      ) : (
        <Paper>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Nombre</TableCell>
                  <TableCell>Descripcion</TableCell>
                  <TableCell>Intervalo</TableCell>
                  <TableCell>Estado</TableCell>
                  <TableCell align="right">Acciones</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {configList.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center">
                      <Typography variant="body2" color="text.secondary" sx={{ py: 4 }}>
                        No hay configuraciones de sincronizacion creadas.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  configList.map((config) => (
                    <TableRow key={config.id} hover>
                      <TableCell>
                        <Typography variant="body2">{config.name}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{
                            maxWidth: 300,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {config.description ?? "\u2014"}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {config.processIntervalSeconds}s
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={config.isActive ? "Activo" : "Inactivo"}
                          color={config.isActive ? "success" : "default"}
                          size="small"
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "flex-end",
                            gap: 0.5,
                          }}
                        >
                          {canEditSync && (
                            <Tooltip title="Editar">
                              <IconButton
                                size="small"
                                onClick={() => setEditConfig(config)}
                                color="primary"
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                          {canEditSync && (
                            <Tooltip title="Backfill / Resync">
                              <IconButton
                                size="small"
                                onClick={() => setBackfillConfig(config)}
                                color="info"
                              >
                                <SyncIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                          {canDeleteSync && (
                            <Tooltip title="Eliminar">
                              <IconButton
                                size="small"
                                onClick={() => setDeleteConfig(config)}
                                color="error"
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      <SyncConfigWizardDialog
        open={createOpen}
        editConfig={null}
        onClose={() => setCreateOpen(false)}
      />
      <SyncConfigWizardDialog
        open={!!editConfig}
        editConfig={editConfig}
        onClose={() => setEditConfig(null)}
      />
      <DeleteConfirmDialog
        open={!!deleteConfig}
        config={deleteConfig}
        onClose={() => setDeleteConfig(null)}
      />
      <BackfillDialog
        open={!!backfillConfig}
        config={backfillConfig}
        onClose={() => setBackfillConfig(null)}
      />
    </Container>
  );
};

export default SyncConfigsPage;
